import React, { FC } from 'react';
import { useRouter } from 'next/compat/router';
import { User as TUser } from 't2-graphql-types';
import { useMutation } from '@apollo/client';
import { createPortal } from 'react-dom';
import { PenIcon } from '../Icons/PenIcon';
import { QuoteHighlightingToolbarButton } from './QuoteHighlightingToolbarButton';
import { useModal } from '../Modal/useModal';
import ConfirmationModalActions from '../ConfirmationModal/ConfirmationModalActions';
import { TCloudinaryImageShape } from '../CloudinaryResponsiveImage/types';
import { createQuoteMutation } from './mutations/createQuoteMutation';
import { ReplyToQuoteModalContent } from '../ReplyToQuoteModal/ReplyToQuoteModalContent';
import { showNotification } from '../NotificationBanner/showNotification';
import { captureException } from '../../services/monitoring/captureException';
import { TArticleHighlight } from '../Highlight/types';
import { getNewArticlePageUrl } from '../../services/pagesUrl/getNewArticlePageUrl';

export type TQuoteHighlightingToolbarProps = {
  articleId: string;
  pickedHighlightData: TArticleHighlight;
  articleTitle: string;
  author: Pick<TUser, 'id' | 'name' | 'username'>;
  articleSlug: string;
  coverImage?: TCloudinaryImageShape | undefined;
  modalContainer: HTMLElement;
};

export const QuoteHighlightingToolbar: FC<TQuoteHighlightingToolbarProps> = ({
  articleId,
  pickedHighlightData,
  articleTitle,
  author,
  articleSlug,
  coverImage,
  modalContainer,
}) => {
  const router = useRouter();
  const [createQuote, { loading }] = useMutation(createQuoteMutation, {
    variables: {
      parentArticleId: articleId,
      quote: pickedHighlightData,
    },
  });

  const {
    ModalComponent: ReplyToQuoteModal,
    openModal: openReplyToQuoteModal,
    closeModal: closeReplyToQuoteModal,
  } = useModal();

  const buttons = [
    {
      key: 'replyToQuote',
      title: 'Reply to Quote',
      leftAdornment: <PenIcon />,
      onClick: openReplyToQuoteModal,
    },
  ];

  const handleClickOnConfirm = async () => {
    try {
      const { data } = await createQuote();
      const createdQuoteId = data.createArticle.id;
      router?.push(getNewArticlePageUrl({ parentArticleId: createdQuoteId }));
    } catch (error) {
      showNotification({
        type: 'error',
        title: '',
        message: 'There was a problem with creating a Reply to Quote.',
      });
      captureException(error);
      closeReplyToQuoteModal();
    }
  };

  return (
    <>
      <div className="max-w-fit flex bg-t2-neutral-grey07 rounded-[8px] border p-2">
        {buttons.map((button) => (
          <QuoteHighlightingToolbarButton
            key={button.key}
            leftAdornment={button.leftAdornment}
            onClick={button.onClick}
          >
            {button.title}
          </QuoteHighlightingToolbarButton>
        ))}
      </div>
      {modalContainer &&
        createPortal(
          <ReplyToQuoteModal
            title="Write a Reply to Quote"
            footer={
              <ConfirmationModalActions
                confirmationButtonText="Continue"
                handleCancel={closeReplyToQuoteModal}
                handleConfirm={handleClickOnConfirm}
                isLoading={loading}
              />
            }
          >
            <ReplyToQuoteModalContent
              id={articleId}
              articleTitle={articleTitle}
              author={author}
              pickedHighlightData={pickedHighlightData}
              slug={articleSlug}
              coverImage={coverImage}
            />
          </ReplyToQuoteModal>,
          modalContainer,
        )}
    </>
  );
};
